/** Pure helpers for Member Detail calendar interactions. */

import { toLocalDateKey } from '../../../../utils/dateKey';

export type MemberCalendarGridDay = {
  ymd: string;
  day: number;
  year: number;
  month: number;
  inCurrentMonth: boolean;
};

/** Monday-start visible range for a month grid (includes adjacent-month spill days). */
export function memberCalendarVisibleRange(
  year: number,
  month: number
): { fromDate: string; toDate: string } {
  const first = new Date(year, month, 1);
  let startDow = first.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1;
  const gridStart = new Date(year, month, 1 - startDow);

  const last = new Date(year, month + 1, 0);
  let endDow = last.getDay();
  endDow = endDow === 0 ? 6 : endDow - 1;
  const gridEnd = new Date(year, month, last.getDate() + (6 - endDow));

  return {
    fromDate: toLocalDateKey(gridStart),
    toDate: toLocalDateKey(gridEnd),
  };
}

/** Continuous Mon–Sun month grid with leading/trailing adjacent-month dates. */
export function buildMemberCalendarGrid(year: number, month: number): MemberCalendarGridDay[] {
  const { fromDate, toDate } = memberCalendarVisibleRange(year, month);
  const [fy, fm, fd] = fromDate.split('-').map(Number);
  const [ty, tm, td] = toDate.split('-').map(Number);
  const cursor = new Date(fy, fm - 1, fd);
  const end = new Date(ty, tm - 1, td);
  const days: MemberCalendarGridDay[] = [];
  while (cursor.getTime() <= end.getTime()) {
    days.push({
      ymd: toLocalDateKey(cursor),
      day: cursor.getDate(),
      year: cursor.getFullYear(),
      month: cursor.getMonth(),
      inCurrentMonth: cursor.getFullYear() === year && cursor.getMonth() === month,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

export function formatSessionCardMeta(
  exerciseCount: number | null | undefined,
  durationMin: number
): string {
  const n =
    exerciseCount == null || exerciseCount < 0 || !Number.isFinite(exerciseCount)
      ? 0
      : Math.floor(exerciseCount);
  const exLabel = n === 1 ? '1 exercise' : `${n} exercises`;
  if (durationMin > 0) return `${exLabel} · ${durationMin} min`;
  return `${exLabel} · —`;
}

/** Narrow calendar cells — keep one row without mid-word wrap. */
export function formatSessionCardMetaCompact(
  exerciseCount: number | null | undefined,
  durationMin: number
): string {
  const n =
    exerciseCount == null || exerciseCount < 0 || !Number.isFinite(exerciseCount)
      ? 0
      : Math.floor(exerciseCount);
  if (durationMin > 0) return `${n} ex · ${durationMin} min`;
  return `${n} ex`;
}

export function formatShortCalendarDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export type { LibraryWorkoutLike } from '../../utils/workoutLibraryFilters';
export {
  filterLibraryWorkouts,
  topFocusTagChips,
} from '../../utils/workoutLibraryFilters';

export function exerciseCountFromWorkout(workout: unknown): number | null {
  if (!workout || typeof workout !== 'object') return null;
  const w = workout as {
    exerciseCount?: number;
    workoutBlocks?: Array<{ exerciseSets?: Array<{ exerciseId?: string }> }>;
  };
  if (typeof w.exerciseCount === 'number' && Number.isFinite(w.exerciseCount)) {
    return Math.max(0, Math.floor(w.exerciseCount));
  }
  if (!Array.isArray(w.workoutBlocks)) return null;
  const ids = new Set<string>();
  w.workoutBlocks.forEach((block) => {
    (block.exerciseSets ?? []).forEach((es) => {
      if (es.exerciseId) ids.add(es.exerciseId);
    });
  });
  return ids.size;
}
